import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAddressEqual } from 'viem';
import { waitForTransactionReceipt } from 'viem/actions';
import { useAccount, useClient, useWriteContract } from 'wagmi';
import { abi } from '../abis/ExchangeGateway';
import { isUserRejectedError } from '../lib/errors';
import { showToast } from '../lib/showToast';
import {
  withdrawClaimingFailedToastMessage,
  withdrawClaimingSuccessToastMessage,
} from '../lib/toasts';
import { useExchangeAddress } from './useDataForSigning';
import { getPendingWithdrawQuery, PendingWithdraw } from './usePendingWithdraw';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { useSupportedTokenBalances } from './useSupportedTokenBalances';
import { useTokenDetails } from './useTokenDetails';

export const useFinalizeWithdraw = () => {
  const queryClient = useQueryClient();
  const client = useClient();
  const { writeContractAsync } = useWriteContract();
  const { queryKey: supportedTokenBalancesQueryKey } = useSupportedTokenBalances();
  const { address: exchangeAddress } = useExchangeAddress();
  const { address } = useAccount();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: tokens } = useTokenDetails();

  return useMutation({
    mutationFn: async (pendingWithdraw: PendingWithdraw) => {
      if (!exchangeAddress || !address || !client) return;
      const finalizeWithdrawHash = await writeContractAsync({
        address: exchangeAddress,
        abi,
        functionName: 'finalizeWithdraw',
        args: [address, pendingWithdraw.subaccount],
      });

      await waitForTransactionReceipt(client, { hash: finalizeWithdrawHash });
      return { pendingWithdraw, address };
    },
    onSuccess: async (result) => {
      if (!result) return;
      const { pendingWithdraw, address } = result;
      queryClient.setQueryData(getPendingWithdrawQuery(selectedSubaccount?.id).queryKey, () => []);
      await queryClient.invalidateQueries({
        queryKey: supportedTokenBalancesQueryKey,
        refetchType: 'all',
      });
      const token = tokens?.find((token) => isAddressEqual(token.address, pendingWithdraw.token));
      if (!token?.erc20Symbol) return;
      const amountAfterFee = pendingWithdraw.amount - token.withdrawFee;
      showToast(withdrawClaimingSuccessToastMessage(address, token.erc20Symbol, amountAfterFee));
    },
    onError: (error) => {
      if (isUserRejectedError(error)) return;
      console.error(error);
      showToast(withdrawClaimingFailedToastMessage());
    },
  });
};
